"use client";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Sidebar from "@/components/Sidebar";
import MiniCalendarCard from "@/components/dashboard/MiniCalendarCard";
import RecentProductsCard from "@/components/dashboard/RecentProductsCard";
import ExternalLinksCard from "@/components/dashboard/ExternalLinksCard";
import SourcingUpsideCard from "@/components/dashboard/SourcingUpsideCard";
import WorkInstructionsCard from "@/components/dashboard/WorkInstructionsCard";
import {
  getDashboardSummary,
  getTodaySummary,
  getTargetAlerts,
  getPendingDeposits,
  confirmDeposit,
  confirmAsDeposit,
  confirmOrderAddonDeposit,
  settleSkuChange,
  confirmReturnReceiptPayment,
  getDaiwaDirectShipPreview,
  getAttentionOrders,
  getAttentionOrderDetail,
  requestAttentionComplete,
  approveAttentionComplete,
  rejectAttentionComplete,
  getRoutineSummary,
  // [ERP-PACKING-SOLDOUT-REQUEST-QUEUE-20260601-001]
  listSoldoutRequests,
  getSoldoutSummary,
  updateSoldoutStatus,
  type SoldoutRequestRow,
  type SoldoutSummary,
  type DashboardSummary,
  type TodaySummary,
  type TargetAlertResponse,
  type PendingDepositItem,
  type DirectShipPreview,
  type AttentionItemRow,
  type AttentionListResponse,
  type AttentionDetailResponse,
  type RoutineSummary,
} from "@/lib/api";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

function fmt(n: number) {
  return n.toLocaleString("ko-KR");
}
// 운영대시보드 한국식 금액 표기: 2,401,180원 / -33,370원 / "-"
function formatKRW(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";
  return Math.round(n).toLocaleString("ko-KR") + "원";
}
function toLocalDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function StatCard({
  label, value, sub, color = "blue", alert = false,
}: {
  label: string; value: string | number; sub?: string; color?: string; alert?: boolean;
}) {
  const colors: Record<string, string> = {
    blue: "bg-blue-50 border-blue-100 text-blue-700",
    green: "bg-green-50 border-green-100 text-green-700",
    purple: "bg-purple-50 border-purple-100 text-purple-700",
    orange: "bg-orange-50 border-orange-100 text-orange-700",
    red: "bg-red-50 border-red-100 text-red-700",
    teal: "bg-teal-50 border-teal-100 text-teal-700",
    gray: "bg-gray-50 border-gray-200 text-gray-600",
    yellow: "bg-yellow-50 border-yellow-100 text-yellow-700",
  };
  return (
    <div className={`rounded-xl border p-4 ${alert ? "border-red-300 bg-red-50" : colors[color] || colors.blue}`}>
      <p className={`text-2xl font-bold ${alert ? "text-red-700" : ""}`}>{typeof value === "number" ? fmt(value) : value}</p>
      <p className={`text-xs font-semibold mt-0.5 ${alert ? "text-red-600" : ""}`}>{label}</p>
      {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">{children}</h2>
  );
}

const ENTITY_LABELS: Record<string, string> = {
  order: "주문",
  as_request: "AS",
  return_receipt: "반품·교환",
  purchase_order: "구매입고",
  inbound_shipment: "구매입고",
};

function entityLabelOf(entityType: string): string {
  return ENTITY_LABELS[entityType] ?? "기타";
}

function attentionStatusLabel(status: string): { text: string; cls: string } {
  switch (status) {
    case "active":
      return { text: "활성", cls: "bg-yellow-100 text-yellow-800" };
    case "completion_requested":
      return { text: "완료요청", cls: "bg-blue-100 text-blue-700" };
    case "completed":
      return { text: "완료", cls: "bg-gray-200 text-gray-600" };
    case "rejected":
      return { text: "반려", cls: "bg-red-100 text-red-700" };
    default:
      return { text: status, cls: "bg-gray-100 text-gray-600" };
  }
}

function formatChatTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("ko-KR", { month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

export default function DashboardPage() {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [todaySummary, setTodaySummary] = useState<TodaySummary | null>(null);
  const [targetAlerts, setTargetAlerts] = useState<TargetAlertResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ── 확인필요 주문건 (26차 후속 #57 Phase 2) ─────────────────────
  const [attentionList, setAttentionList] = useState<AttentionListResponse | null>(null);
  const [attentionBusyId, setAttentionBusyId] = useState<number | null>(null);
  const [attentionError, setAttentionError] = useState<string | null>(null);
  const [oldModalOpen, setOldModalOpen] = useState(false);
  const [detailModalId, setDetailModalId] = useState<number | null>(null);
  const [attentionDetail, setAttentionDetail] = useState<AttentionDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── 계좌입금 실행 큐 (승인요청과 분리) ───────────────────────
  const [pendingDeposits, setPendingDeposits] = useState<PendingDepositItem[]>([]);
  const [pendingDepositsTotal, setPendingDepositsTotal] = useState(0);
  const [confirmedDepositsTotal, setConfirmedDepositsTotal] = useState(0);
  // [ERP-PAYMENT-CONFIRM-QUEUE-TABS-AND-ZERO-AMOUNT-FILTER-20260529-001]
  // 입금확인 큐 탭 — pending / completed (다이와 주문건 카드와 동일 패턴).
  const [depositQueueTab, setDepositQueueTab] = useState<"pending" | "completed">("pending");
  const [pendingDepositsAmount, setPendingDepositsAmount] = useState(0);
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  // ── 다이와 직배건 ────────────────────────────────────────────
  const [daiwaDsPreview, setDaiwaDsPreview] = useState<DirectShipPreview | null>(null);
  const [daiwaDsModal, setDaiwaDsModal] = useState(false);

  // ── [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-001] 오늘 해야 할 업무 카드 ──
  const [routineSummary, setRoutineSummary] = useState<RoutineSummary | null>(null);

  // ── [ERP-PACKING-SOLDOUT-REQUEST-QUEUE-20260601-001] 품절 처리 요청 큐 ──
  const [soldoutRequests, setSoldoutRequests] = useState<SoldoutRequestRow[]>([]);
  const [soldoutSummary, setSoldoutSummary] = useState<SoldoutSummary | null>(null);
  const [soldoutBusyId, setSoldoutBusyId] = useState<number | null>(null);

  const loadSoldout = async () => {
    try {
      const [list, sum] = await Promise.all([
        listSoldoutRequests({ status: "pending,in_progress", limit: 10 }),
        getSoldoutSummary(),
      ]);
      setSoldoutRequests(list.items);
      setSoldoutSummary(sum);
    } catch { /* ignore */ }
  };

  const handleSoldoutStatus = async (id: number, status: string) => {
    const labels: Record<string, string> = { in_progress: "처리중으로 변경", completed: "완료 처리", rejected: "반려" };
    if (!window.confirm(`품절 요청 #${id} 을(를) ${labels[status] ?? status} 하시겠습니까?\n상품/재고는 자동 변경되지 않습니다.`)) return;
    setSoldoutBusyId(id);
    try {
      await updateSoldoutStatus(id, { status });
      await loadSoldout();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "상태 변경 실패");
    } finally {
      setSoldoutBusyId(null);
    }
  };

  const today = toLocalDateStr(new Date());
  const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
  const [selectedDate, setSelectedDate] = useState(today);

  useEffect(() => {
    if (!localStorage.getItem("access_token")) router.replace("/login");
    setMounted(true);
  }, [router]);

  const loadAttention = async () => {
    try {
      const res = await getAttentionOrders();
      setAttentionList(res);
      setAttentionError(null);
    } catch (e: unknown) {
      setAttentionError(e instanceof Error ? e.message : "확인필요 주문건 로드 실패");
    }
  };

  const openAttentionDetail = async (id: number) => {
    setDetailModalId(id);
    setAttentionDetail(null);
    setDetailLoading(true);
    try {
      const res = await getAttentionOrderDetail(id);
      setAttentionDetail(res);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "상세 로드 실패");
      setDetailModalId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRequestComplete = async (id: number) => {
    if (!window.confirm("이 주문건의 이슈를 완료 요청하시겠습니까?")) return;
    setAttentionBusyId(id);
    try {
      await requestAttentionComplete(id);
      await loadAttention();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "완료 요청 실패");
    } finally {
      setAttentionBusyId(null);
    }
  };

  const handleApproveComplete = async (id: number) => {
    if (!window.confirm("이 항목을 완료 처리합니다. 계속할까요?")) return;
    setAttentionBusyId(id);
    try {
      await approveAttentionComplete(id);
      await loadAttention();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "승인 실패");
    } finally {
      setAttentionBusyId(null);
    }
  };

  const handleRejectComplete = async (id: number) => {
    const reason = window.prompt("반려 사유 (선택)");
    if (reason === null) return;
    setAttentionBusyId(id);
    try {
      await rejectAttentionComplete(id, reason || undefined);
      await loadAttention();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "반려 실패");
    } finally {
      setAttentionBusyId(null);
    }
  };

  const loadPendingDeposits = async () => {
    try {
      const res = await getPendingDeposits(50);
      setPendingDeposits(res.items);
      setPendingDepositsTotal(res.pending_total);
      setConfirmedDepositsTotal(res.confirmed_total);
      setPendingDepositsAmount(res.pending_amount_total || 0);
    } catch { /* ignore */ }
  };

  const loadDaiwaDsPreview = async () => {
    try {
      // 분류별 카운트 + 샘플 50행 (대시보드 카드 + 모달 공용).
      const res = await getDaiwaDirectShipPreview({}, 50);
      setDaiwaDsPreview(res);
    } catch { /* ignore */ }
  };

  // 입금확인 완료 — 관리자 전용 (비-관리자는 버튼이 애초에 보이지 않음).
  // 26차 후속 #25 — 처리 전 confirm() 팝업으로 구분/고객/금액 재확인.
  // 26차 후속 — 입금 확인 큐 통합 Phase 1: return_exchange 분기 추가.
  const handleConfirmDeposit = async (item: PendingDepositItem) => {
    const key =
      item.kind === "order"
        ? `order:${item.order_no}`
        : item.kind === "return_exchange"
          ? `return:${item.return_receipt_id}`
          : item.kind === "order_addon"
            ? `addon:${item.addon_item_id}`
            : item.kind === "sku_change"
              ? `sku_change:${item.sku_change_id}`
              : `as:${item.as_id}`;
    const refNo =
      item.kind === "order"
        ? item.order_no
        : item.kind === "return_exchange"
          ? item.order_no || `RR#${item.return_receipt_id}`
          : item.kind === "order_addon" || item.kind === "sku_change"
            ? item.order_no
            : item.as_no;
    const amountStr = item.amount ? item.amount.toLocaleString() + "원" : "—";
    const ok = window.confirm(
      `[${item.source_label}] 입금 확인 처리\n\n` +
      `· 번호 : ${refNo || "—"}\n` +
      `· 고객 : ${item.customer_name || "—"}${item.customer_phone ? " (" + item.customer_phone + ")" : ""}\n` +
      `· 금액 : ${amountStr}\n\n` +
      `처리 시 원본 도메인의 상태가 갱신됩니다. 계속하시겠습니까?`
    );
    if (!ok) return;
    setConfirmingKey(key);
    try {
      if (item.kind === "order" && item.order_no) {
        await confirmDeposit(item.order_no);
      } else if (item.kind === "as" && item.as_id) {
        await confirmAsDeposit(item.as_id);
      } else if (item.kind === "return_exchange") {
        if (!item.return_receipt_id) {
          throw new Error("return_receipt_id 가 없어 입금 확인을 처리할 수 없습니다.");
        }
        await confirmReturnReceiptPayment(item.return_receipt_id);
      } else if (item.kind === "order_addon") {
        // [ERP-ORDER-DETAIL-ADDON-COMBINE-SHIPMENT-PAYMENT-QUEUE-DEV-20260703-032]
        if (!item.addon_item_id) {
          throw new Error("addon_item_id 가 없어 입금 확인을 처리할 수 없습니다.");
        }
        await confirmOrderAddonDeposit(item.addon_item_id);
      } else if (item.kind === "sku_change") {
        // [DASH-SKU-CHANGE-DEPOSIT] 구매상품 변경 추가금 — 주문 상세 [입금확인]과 같은 정산 처리(정산완료).
        if (!item.sku_change_id) {
          throw new Error("sku_change_id 가 없어 입금 확인을 처리할 수 없습니다.");
        }
        await settleSkuChange(item.sku_change_id);
      } else {
        throw new Error(`지원하지 않는 항목 유형: kind=${item.kind}`);
      }
      await loadPendingDeposits();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "처리 실패");
    } finally {
      setConfirmingKey(null);
    }
  };

  const load = async (d: string) => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const [dashboard, ts, alerts] = await Promise.all([
        getDashboardSummary(d),
        getTodaySummary(),
        getTargetAlerts(now.getFullYear(), now.getMonth() + 1).catch(() => null),
      ]);
      setData(dashboard);
      setTodaySummary(ts);
      setTargetAlerts(alerts);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mounted) {
      load(selectedDate);
      loadAttention();
      loadPendingDeposits();
      loadDaiwaDsPreview();
      loadSoldout();
      // [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-001] 루틴 카드 카운트.
      // 실패해도 카드는 카운트 없이 렌더 — silent catch.
      getRoutineSummary()
        .then(setRoutineSummary)
        .catch(() => setRoutineSummary(null));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  const handleDateChange = (d: string) => {
    setSelectedDate(d);
    load(d);
  };

  const handleCsvDownload = async () => {
    const token = localStorage.getItem("access_token");
    const res = await fetch(
      `${API_BASE}/dashboard/daily-report?date=${selectedDate}&format=csv`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) { alert("CSV 다운로드 실패"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `daily_report_${selectedDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!mounted) return null;

  const s = data;

  return (
    <div className="flex min-h-screen bg-gray-50 print:block">
      <style>{`
        @media print {
          aside { display: none !important; }
          .no-print { display: none !important; }
          body { background: white; }
          main { padding: 16px !important; }
          .print\\:block { display: block !important; }
          .max-w-6xl { max-width: 100% !important; }
          .max-w-\\[1800px\\] { max-width: 100% !important; }
          .grid-cols-7 { grid-template-columns: repeat(7, 1fr); }
          .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
          .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
          .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
          section { break-inside: avoid; }
          .rounded-xl { border-radius: 0 !important; }
        }
      `}</style>
      <Sidebar />
      <main className="flex-1 p-6 xl:px-8 2xl:px-10 overflow-y-auto print:p-4">
        {/* [ERP-DASHBOARD-WIDE-LAYOUT-READABILITY-20260529-001]
            기존 max-w-6xl(1152px) → 넓은 화면 가로폭 적극 활용 (max-w-[1800px]). */}
        <div className="w-full max-w-[1800px] mx-auto">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">운영 대시보드</h1>
              <p className="text-sm text-gray-500 mt-0.5">일일 마감 리포트 — 핵심 운영 수치 한눈에 보기</p>
            </div>
            <div className="flex items-center gap-2 no-print">
              <button
                onClick={() => handleDateChange(yesterday)}
                className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                  selectedDate === yesterday ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                어제
              </button>
              <button
                onClick={() => handleDateChange(today)}
                className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                  selectedDate === today ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                오늘
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => load(selectedDate)}
                className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-colors"
              >
                새로고침
              </button>
              <div className="w-px h-5 bg-gray-300 mx-1" />
              <button
                onClick={handleCsvDownload}
                disabled={!s}
                className="px-3 py-1.5 text-xs bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white rounded-lg font-medium transition-colors"
              >
                ⬇ CSV
              </button>
              <button
                onClick={() => window.print()}
                disabled={!s}
                className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-800 disabled:opacity-40 text-white rounded-lg font-medium transition-colors"
              >
                🖨 PDF/인쇄
              </button>
            </div>
          </div>

          {/* ── [ERP-DASHBOARD-STAFF-WORK-INSTRUCTIONS-MVP-20260622-001] 업무지시 ── */}
          <WorkInstructionsCard />

          {/* ── [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-001] 오늘 해야 할 업무 ── */}
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-sm font-bold text-gray-800">🗂️ 오늘 해야 할 업무</h2>
              <span className="text-[10px] text-gray-400">매일 반복 업무 바로가기</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {/* A. 토글 주문 업로드 */}
              <Link href="/orders/import" className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow transition">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-gray-900">📤 토글 주문 업로드</span>
                </div>
                <p className="text-[11px] text-gray-500 mb-2">매일 토글 주문 파일을 업로드합니다.</p>
                <span className="inline-block text-xs px-2 py-1 rounded bg-blue-600 text-white">바로 업로드</span>
              </Link>

              {/* B. 포장출고 */}
              <Link href="/picking" className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow transition">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-gray-900">📦 포장 출고</span>
                </div>
                <p className="text-[11px] text-gray-500 mb-2">송장 스캔 / 상품 스캔 / 출고 처리.</p>
                <span className="inline-block text-xs px-2 py-1 rounded bg-blue-600 text-white">포장 시작</span>
              </Link>

              {/* C. 출고중지 요청 */}
              <Link href="/shipment-holds" className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow transition">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-gray-900">🚫 출고중지 요청</span>
                  {routineSummary && routineSummary.shipment_holds_pending > 0 ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                      대기 {routineSummary.shipment_holds_pending}건
                    </span>
                  ) : null}
                </div>
                <p className="text-[11px] text-gray-500 mb-2">출고중지 요청 처리.</p>
                <span className="inline-block text-xs px-2 py-1 rounded bg-gray-700 text-white">바로가기</span>
              </Link>

              {/* D. 다이와 재고 업로드 */}
              <Link href="/daiwa" className="block bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow transition">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-bold text-gray-900">📊 다이와 재고 업로드</span>
                </div>
                <p className="text-[11px] text-gray-500 mb-2">다이와 JAN / 재고표 업로드.</p>
                <span className="inline-block text-xs px-2 py-1 rounded bg-blue-600 text-white">업로드</span>
              </Link>

              {/* [ERP-OPERATIONS-DASHBOARD-ROUTINE-20260519-002] 사용자 피드백 —
                  네이버 등록리스트 / PRODUCT_MASTER / 바코드 미등록 / 계정 승인 /
                  휴가 승인 5 카드 제거. 남는 4 카드: 토글 업로드 / 포장 / 출고중지 /
                  다이와 재고. routineSummary 는 shipment_holds_pending 뱃지에만 사용. */}
            </div>
          </section>

          {/* 26차 후속 — 운영대시보드 위젯 1차: 미니달력 / 금주 신상품 / 주요 로그인 / 소싱 업사이드 */}
          <div
            data-testid="dashboard-widgets-row-1"
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6 no-print"
          >
            <MiniCalendarCard />
            <RecentProductsCard />
            <ExternalLinksCard />
            <SourcingUpsideCard />
          </div>

          {loading && (
            <div className="flex items-center justify-center py-20">
              <p className="text-sm text-gray-400">불러오는 중...</p>
            </div>
          )}

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
          )}

          {/* ── 확인필요 주문건 (운영대시보드 카드, 26차 후속 #57 Phase 2) ── */}
          {mounted && (() => {
            const isAdmin = (typeof window !== "undefined" ? localStorage.getItem("user_role") : "") === "admin";
            const list = attentionList;
            const activeRows = list?.active ?? [];
            const requestedRows = list?.completion_requested ?? [];
            const rows: AttentionItemRow[] = [...activeRows, ...requestedRows];
            const counts = list?.counts ?? { active: 0, completion_requested: 0, completed_total: 0 };

            return (
              <section className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">📌 확인필요 주문건</h2>
                  {counts.active > 0 && (
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full">
                      활성 {counts.active}건
                    </span>
                  )}
                  {counts.completion_requested > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">
                      완료요청 {counts.completion_requested}건
                    </span>
                  )}
                  <button
                    onClick={() => setOldModalOpen(true)}
                    className="ml-auto text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    완료된 항목 (최근 50)
                  </button>
                  <button
                    onClick={() => loadAttention()}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    title="새로고침"
                  >
                    ↻
                  </button>
                </div>

                {attentionError && (
                  <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                    {attentionError}
                  </div>
                )}

                <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                  {rows.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-400">
                      확인필요 주문건이 없습니다.
                    </div>
                  ) : (
                    <table className="w-full min-w-[1100px] text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {[
                            "업무",
                            "주문/대상번호",
                            "고객",
                            "요약/상품",
                            "최근 메시지",
                            "등록",
                            "갱신",
                            "상태",
                            "작성자",
                            "액션",
                          ].map((h) => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((row) => {
                          const st = attentionStatusLabel(row.status);
                          const isRequested = row.status === "completion_requested";
                          const busy = attentionBusyId === row.id;
                          const refNo = row.order_no || row.target_no || row.entity_id;
                          return (
                            <tr key={row.id} className={isRequested ? "bg-blue-50/40 hover:bg-blue-50" : "hover:bg-gray-50"}>
                              <td className="px-3 py-2">
                                <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-bold">
                                  {row.work_type || entityLabelOf(row.entity_type)}
                                </span>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs text-gray-700 max-w-[200px] truncate" title={refNo}>
                                {refNo}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-700">{row.customer_name || "—"}</td>
                              <td className="px-3 py-2 text-xs text-gray-700 max-w-[380px]">
                                <div className="line-clamp-2 font-medium leading-snug" title={row.product_summary || row.summary || ""}>
                                  {row.product_summary || row.summary || "—"}
                                </div>
                                {(row.product_summary && row.summary) && (
                                  <div className="line-clamp-1 text-[11px] text-gray-400 mt-0.5" title={row.summary}>
                                    {row.summary}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 max-w-[280px]">
                                <span className="line-clamp-1">{row.last_message_text || "—"}</span>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                {formatChatTime(row.created_at)}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                                {row.last_message_at
                                  ? formatChatTime(row.last_message_at)
                                  : formatChatTime(row.updated_at)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${st.cls}`}>
                                  {st.text}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">
                                {row.requested_by_name || row.created_by_name || "—"}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => openAttentionDetail(row.id)}
                                    className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                                  >
                                    상세
                                  </button>
                                  {row.status === "active" && (
                                    <button
                                      disabled={busy}
                                      onClick={() => handleRequestComplete(row.id)}
                                      className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50"
                                    >
                                      {busy ? "..." : "완료요청"}
                                    </button>
                                  )}
                                  {isAdmin && isRequested && (
                                    <>
                                      <button
                                        disabled={busy}
                                        onClick={() => handleApproveComplete(row.id)}
                                        className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
                                      >
                                        승인
                                      </button>
                                      <button
                                        disabled={busy}
                                        onClick={() => handleRejectComplete(row.id)}
                                        className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded disabled:opacity-50"
                                      >
                                        반려
                                      </button>
                                    </>
                                  )}
                                  {isAdmin && row.status === "active" && (
                                    <button
                                      disabled={busy}
                                      onClick={() => handleApproveComplete(row.id)}
                                      className="px-2 py-1 text-xs bg-green-600/80 hover:bg-green-700 text-white rounded disabled:opacity-50"
                                      title="관리자 즉시 완료"
                                    >
                                      즉시완료
                                    </button>
                                  )}
                                  {row.detail_url && (
                                    <Link
                                      href={row.detail_url}
                                      className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800"
                                    >
                                      이동 →
                                    </Link>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            );
          })()}

          {/* 상세 모달 */}
          {detailModalId !== null && (
            /* [ERP-GLOBAL-MODAL-OUTSIDE-CLICK-CLOSE-DISABLE-DEV-20260623-001] overlay 클릭 닫기 제거 */
            <div
              className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            >
              <div
                className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">확인필요 주문건 상세</h3>
                  <button
                    onClick={() => { setDetailModalId(null); setAttentionDetail(null); }}
                    className="text-gray-400 hover:text-gray-600 text-lg"
                  >×</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5">
                  {detailLoading && <p className="text-sm text-gray-400">불러오는 중...</p>}
                  {!detailLoading && attentionDetail && (() => {
                    const it = attentionDetail.item;
                    const st = attentionStatusLabel(it.status);
                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div><span className="text-gray-400">업무: </span><span className="font-bold">{it.work_type || entityLabelOf(it.entity_type)}</span></div>
                          <div><span className="text-gray-400">상태: </span><span className={`px-2 py-0.5 rounded ${st.cls}`}>{st.text}</span></div>
                          <div><span className="text-gray-400">주문/대상: </span><span className="font-mono">{it.order_no || it.target_no || it.entity_id}</span></div>
                          <div><span className="text-gray-400">고객: </span>{it.customer_name || "—"}</div>
                          <div><span className="text-gray-400">등록: </span>{formatChatTime(it.created_at)} {it.created_by_name && `· ${it.created_by_name}`}</div>
                          <div><span className="text-gray-400">갱신: </span>{formatChatTime(it.updated_at)}</div>
                          {it.requested_at && (
                            <div><span className="text-gray-400">완료요청: </span>{formatChatTime(it.requested_at)} · {it.requested_by_name || "—"}</div>
                          )}
                          {it.approved_at && (
                            <div><span className="text-gray-400">승인: </span>{formatChatTime(it.approved_at)} · {it.approved_by_name || "—"}</div>
                          )}
                          {it.rejected_at && (
                            <div className="col-span-2"><span className="text-gray-400">반려: </span>{formatChatTime(it.rejected_at)} · {it.rejected_by_name || "—"}</div>
                          )}
                        </div>
                        {it.product_summary && (
                          <div className="px-3 py-2 bg-amber-50 rounded text-xs text-gray-800">
                            <div className="text-gray-500 mb-1">상품</div>
                            <div className="whitespace-pre-wrap font-medium">{it.product_summary}</div>
                          </div>
                        )}
                        {it.summary && (
                          <div className="px-3 py-2 bg-gray-50 rounded text-xs text-gray-700">
                            <div className="text-gray-400 mb-1">요약</div>
                            <div className="whitespace-pre-wrap">{it.summary}</div>
                          </div>
                        )}
                        <div>
                          <div className="text-xs font-bold text-gray-500 mb-2">메시지 이력 ({attentionDetail.messages.length})</div>
                          <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                            {attentionDetail.messages.length === 0 ? (
                              <div className="px-3 py-6 text-center text-xs text-gray-400">메시지 없음</div>
                            ) : (
                              attentionDetail.messages.map((m) => (
                                <div key={m.id} className={`px-3 py-2 text-xs ${m.is_system ? "bg-blue-50/40" : ""}`}>
                                  <div className="flex items-center justify-between text-gray-500 mb-0.5">
                                    <span className="font-bold text-gray-700">{m.sender_name}</span>
                                    <span>{m.created_at ? formatChatTime(m.created_at) : ""}</span>
                                  </div>
                                  <div className="text-gray-800 whitespace-pre-wrap">{m.message}</div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        {it.detail_url && (
                          <div className="pt-2 border-t border-gray-100">
                            <Link
                              href={it.detail_url}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              onClick={() => { setDetailModalId(null); setAttentionDetail(null); }}
                            >
                              주문 상세로 이동 →
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* 완료된 항목 (최근 50) 모달 */}
          {oldModalOpen && (
            /* [ERP-GLOBAL-MODAL-OUTSIDE-CLICK-CLOSE-DISABLE-DEV-20260623-001] overlay 클릭 닫기 제거 */
            <div
              className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
            >
              <div
                className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-sm">
                    완료된 확인필요 주문건 (최근 50 / 누적 {attentionList?.counts.completed_total ?? 0}건)
                  </h3>
                  <button
                    onClick={() => setOldModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 text-lg"
                  >×</button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {(attentionList?.recent_old ?? []).length === 0 ? (
                    <div className="px-5 py-8 text-center text-sm text-gray-400">완료된 항목이 없습니다.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                        <tr>
                          {["업무", "주문/대상", "고객", "요약", "완료시각", "승인자", "액션"].map((h) => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(attentionList?.recent_old ?? []).map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-xs">{row.work_type || entityLabelOf(row.entity_type)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{row.order_no || row.target_no || row.entity_id}</td>
                            <td className="px-3 py-2 text-xs">{row.customer_name || "—"}</td>
                            <td className="px-3 py-2 text-xs max-w-[240px]"><span className="line-clamp-1" title={row.product_summary || row.summary || ""}>{row.product_summary || row.summary || "—"}</span></td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {row.completed_at ? formatChatTime(row.completed_at) : "—"}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600">{row.approved_by_name || "—"}</td>
                            <td className="px-3 py-2">
                              <button
                                onClick={() => { setOldModalOpen(false); openAttentionDetail(row.id); }}
                                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded"
                              >
                                상세
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── [ERP-PACKING-SOLDOUT-REQUEST-QUEUE-20260601-001] 품절 처리 요청 ── */}
          {mounted && soldoutSummary && (soldoutSummary.pending_count > 0 || soldoutSummary.in_progress_count > 0) && (
            <section className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">🚫 품절 처리 요청</h2>
                {soldoutSummary.pending_count > 0 && (
                  <span className="px-2 py-0.5 bg-rose-100 text-rose-700 text-xs font-bold rounded-full">대기 {soldoutSummary.pending_count}</span>
                )}
                {soldoutSummary.in_progress_count > 0 && (
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">처리중 {soldoutSummary.in_progress_count}</span>
                )}
                {soldoutSummary.stale_count > 0 && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">오래된 {soldoutSummary.stale_count}</span>
                )}
                <button onClick={loadSoldout} className="ml-auto text-xs text-gray-500 hover:text-gray-700" title="새로고침">↻</button>
              </div>
              <div className="bg-white rounded-xl border border-rose-200 overflow-x-auto">
                {soldoutRequests.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">처리할 품절 요청이 없습니다.</div>
                ) : (
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="bg-rose-50 border-b border-rose-100">
                      <tr>
                        {["SKU", "상품/옵션", "주문/송장", "요청자", "요청시각", "현재고", "사유/메모", "상태", "액션"].map((h) => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {soldoutRequests.map((r) => {
                        const busy = soldoutBusyId === r.id;
                        return (
                          <tr key={r.id} className="hover:bg-rose-50/40 align-top">
                            <td className="px-3 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">{r.sku}</td>
                            <td className="px-3 py-2 text-xs text-gray-800 max-w-[220px]">
                              <div className="line-clamp-2">{r.product_name_snapshot || "—"}</div>
                              {r.option_name_snapshot && <div className="text-[11px] text-gray-400">{r.option_name_snapshot}</div>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {r.order_no || "—"}<br /><span className="text-gray-400">{r.invoice_no || ""}</span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{r.requested_by_name_snapshot || "—"}</td>
                            <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">{formatChatTime(r.requested_at)}</td>
                            <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{r.current_stock_snapshot ?? "—"}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 max-w-[200px]">
                              <span className="text-rose-700">{r.reason}</span>
                              {r.memo && <div className="line-clamp-1 text-gray-400">{r.memo}</div>}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${r.status === "pending" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                                {r.status === "pending" ? "대기" : "처리중"}
                              </span>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                {r.status === "pending" && (
                                  <button disabled={busy} onClick={() => handleSoldoutStatus(r.id, "in_progress")}
                                    className="px-2 py-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded disabled:opacity-50">처리중</button>
                                )}
                                <button disabled={busy} onClick={() => handleSoldoutStatus(r.id, "completed")}
                                  className="px-2 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50">완료</button>
                                <button disabled={busy} onClick={() => handleSoldoutStatus(r.id, "rejected")}
                                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded disabled:opacity-50">반려</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          )}

          {/* ── 계좌입금 대기 + 다이와 직배건 ── */}
          {/* [ERP-PAYMENT-CONFIRM-QUEUE-TABS-AND-ZERO-AMOUNT-FILTER-20260529-001]
              입금대기/입금완료 탭 도입으로 confirmed 만 있어도 카드 표시 — pending 0 일 때도
              완료 탭 조회 가능하게 가시성 가드 완화. */}
          {mounted && (pendingDepositsTotal > 0 || confirmedDepositsTotal > 0 || (daiwaDsPreview && daiwaDsPreview.total_orders > 0)) && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-8">

              {/* A) 계좌입금 실행 큐 — 승인요청과 분리.
                      pending: 입금확인 필요 (계속 노출).
                      confirmed: 완료 (출고 전 + 3일 이내만 노출). */}
              {(() => {
                const isAdmin = (typeof window !== "undefined" ? localStorage.getItem("user_role") : "") === "admin";
                const pendingItems = pendingDeposits.filter(i => i.state === "pending");
                const confirmedItems = pendingDeposits.filter(i => i.state === "confirmed");
                const itemKey = (i: PendingDepositItem) =>
                  i.kind === "order"
                    ? `order:${i.order_no}`
                    : i.kind === "return_exchange"
                      ? `return:${i.return_receipt_id}`
                      : i.kind === "order_addon"
                        ? `addon:${i.addon_item_id}`
                        : i.kind === "sku_change"
                          ? `sku_change:${i.sku_change_id}`
                          : `as:${i.as_id}`;
                // 26차 후속 #25 — 출처별 배지 색상 (단건/오프라인/AS 시각 구분)
                // [ERP-ORDER-DETAIL-ADDON-COMBINE-SHIPMENT-PAYMENT-QUEUE-DEV-20260703-032] order_addon 배지 추가
                const sourceBadgeClass = (st: string) => {
                  switch (st) {
                    case "manual_order":  return "bg-indigo-100 text-indigo-700";
                    case "offline_store": return "bg-orange-100 text-orange-700";
                    case "as_repair":     return "bg-purple-100 text-purple-700";
                    case "order_addon":   return "bg-teal-100 text-teal-700";
                    case "sku_change_additional": return "bg-pink-100 text-pink-700";
                    default:              return "bg-gray-100 text-gray-700";
                  }
                };
                const renderRow = (item: PendingDepositItem) => {
                  const key = itemKey(item);
                  const idLabel =
                    item.kind === "order"
                      ? item.order_no
                      : item.kind === "return_exchange"
                        ? item.order_no || (item.return_receipt_id != null ? `RR#${item.return_receipt_id}` : null)
                        : item.kind === "order_addon" || item.kind === "sku_change"
                          ? item.order_no
                          : item.as_no;
                  // 26차 후속 — Phase 1: confirmed 우측 라벨을 source 별로 분리.
                  const confirmedLabel =
                    item.kind === "return_exchange"
                      ? "반품/교환 처리 대기"
                      : item.kind === "as"
                        ? "AS 처리 대기"
                        : "출고 대기";
                  return (
                    <div key={key} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${sourceBadgeClass(item.source_type)}`}>
                            {item.source_label}
                          </span>
                          <span className="font-mono text-xs text-indigo-600 font-semibold">{idLabel || "—"}</span>
                          <span className="text-xs text-gray-700 font-medium">{item.customer_name || "—"}</span>
                          {item.customer_phone && (
                            <span className="text-xs text-gray-500">{item.customer_phone}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-sm font-bold text-gray-800">
                            {item.amount ? item.amount.toLocaleString() + "원" : "—"}
                          </span>
                          {item.state === "confirmed" && item.confirmed_at && (
                            <span className="text-xs text-green-700">확인 {item.confirmed_at}</span>
                          )}
                          {item.state === "pending" && item.ref_date && (
                            <span className="text-xs text-gray-400">{item.ref_date}</span>
                          )}
                          {item.detail_url && (
                            <button
                              onClick={() => router.push(item.detail_url!)}
                              className="text-[11px] text-blue-600 hover:text-blue-800 underline"
                            >
                              상세보기
                            </button>
                          )}
                        </div>
                        {item.memo && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{item.memo}</p>
                        )}
                      </div>
                      {/* 관리자 전용 버튼 — 비관리자는 상태만 확인 */}
                      {item.state === "pending" && isAdmin && (
                        <button
                          type="button"
                          onClick={() => handleConfirmDeposit(item)}
                          disabled={confirmingKey === key}
                          className="ml-3 px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg transition-colors whitespace-nowrap"
                        >
                          {confirmingKey === key ? "처리 중..." : "입금 확인"}
                        </button>
                      )}
                      {item.state === "confirmed" && (
                        <span className="ml-3 px-2 py-1 text-[10px] font-bold bg-green-100 text-green-800 rounded">{confirmedLabel}</span>
                      )}
                    </div>
                  );
                };
                return (
                  <section className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-200">
                      <div className="flex items-center gap-2">
                        <span className="text-base">💰</span>
                        <h2 className="text-sm font-bold text-amber-800">입금 확인 큐</h2>
                        {pendingDepositsTotal > 0 && (
                          <span className="px-2 py-0.5 bg-amber-200 text-amber-900 text-xs font-bold rounded-full">
                            필요 {pendingDepositsTotal}
                          </span>
                        )}
                        {pendingDepositsAmount > 0 && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold rounded-full">
                            합계 {pendingDepositsAmount.toLocaleString()}원
                          </span>
                        )}
                        {confirmedDepositsTotal > 0 && (
                          <span className="px-2 py-0.5 bg-green-200 text-green-900 text-xs font-bold rounded-full">
                            완료 {confirmedDepositsTotal}
                          </span>
                        )}
                      </div>
                      <button onClick={loadPendingDeposits} className="text-xs text-amber-600 hover:text-amber-800">
                        새로고침
                      </button>
                    </div>
                    {/* [ERP-PAYMENT-CONFIRM-QUEUE-TABS-AND-ZERO-AMOUNT-FILTER-20260529-001]
                        탭 분리 — pending / completed. 다이와 주문건 카드와 동일 패턴.
                        0원 주문은 backend 에서 pending 큐에서 제외됨 (방어층 client filter 도 동일). */}
                    <div className="flex border-b border-amber-200">
                      <button
                        type="button"
                        onClick={() => setDepositQueueTab("pending")}
                        className={`flex-1 px-4 py-2 text-xs font-bold ${
                          depositQueueTab === "pending"
                            ? "bg-amber-100 text-amber-900 border-b-2 border-amber-600"
                            : "bg-white text-amber-700 hover:bg-amber-50"
                        }`}
                      >
                        입금대기 {pendingItems.length > 0 ? `(${pendingItems.length})` : ""}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDepositQueueTab("completed")}
                        className={`flex-1 px-4 py-2 text-xs font-bold ${
                          depositQueueTab === "completed"
                            ? "bg-green-100 text-green-900 border-b-2 border-green-600"
                            : "bg-white text-green-700 hover:bg-green-50"
                        }`}
                      >
                        입금완료 {confirmedItems.length > 0 ? `(${confirmedItems.length})` : ""}
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {depositQueueTab === "pending" ? (
                        pendingItems.length === 0 ? (
                          <p className="px-4 py-6 text-sm text-gray-400 text-center">
                            입금확인이 필요한 주문이 없습니다.
                          </p>
                        ) : (
                          <div className="divide-y divide-gray-100">{pendingItems.map(renderRow)}</div>
                        )
                      ) : confirmedItems.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-gray-400 text-center">
                          최근 입금완료 내역이 없습니다.
                        </p>
                      ) : (
                        <>
                          <div className="px-4 py-1 text-[10px] text-green-700 bg-green-50/50 text-right">
                            최근 {confirmedItems.length}건
                          </div>
                          <div className="divide-y divide-gray-100">{confirmedItems.map(renderRow)}</div>
                        </>
                      )}
                    </div>
                  </section>
                );
              })()}

              {/* B) 다이와 주문건 — 분류(우리샵/다이와/결정 필요) 중심 */}
              <section className="bg-white rounded-xl border border-blue-200 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-200">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🚢</span>
                    <h2 className="text-sm font-bold text-blue-800">다이와 주문건</h2>
                    {daiwaDsPreview && daiwaDsPreview.total_orders > 0 && (
                      <span className="px-2 py-0.5 bg-blue-200 text-blue-900 text-xs font-bold rounded-full">
                        {daiwaDsPreview.total_orders}건
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={loadDaiwaDsPreview}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      새로고침
                    </button>
                    {daiwaDsPreview && daiwaDsPreview.total_orders > 0 && (
                      <button
                        onClick={() => setDaiwaDsModal(true)}
                        className="text-xs text-blue-700 font-semibold hover:text-blue-900 underline"
                      >
                        전체 보기
                      </button>
                    )}
                  </div>
                </div>
                {/* 분류별 요약 — 운영자가 한눈에 무엇을 처리해야 할지 안다 */}
                {daiwaDsPreview && daiwaDsPreview.class_counts && (
                  <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
                    <a href="/daiwa?class=hitcamp" className="px-3 py-2 text-center hover:bg-emerald-50">
                      <p className="text-base font-bold text-emerald-700">{daiwaDsPreview.class_counts.hitcamp}</p>
                      <p className="text-[10px] text-emerald-600 font-semibold">우리샵 출고</p>
                    </a>
                    <a href="/daiwa?class=daiwa" className="px-3 py-2 text-center hover:bg-blue-50">
                      <p className="text-base font-bold text-blue-700">{daiwaDsPreview.class_counts.daiwa}</p>
                      <p className="text-[10px] text-blue-600 font-semibold">다이와 직배</p>
                    </a>
                    <a href="/daiwa?class=decide" className="px-3 py-2 text-center hover:bg-amber-50">
                      <p className="text-base font-bold text-amber-700">{daiwaDsPreview.class_counts.decide}</p>
                      <p className="text-[10px] text-amber-600 font-semibold">결정 필요</p>
                    </a>
                  </div>
                )}
                {!daiwaDsPreview || daiwaDsPreview.total_orders === 0 ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">처리할 다이와 주문 없음</p>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {daiwaDsPreview.sample_rows.slice(0, 8).map((row, i) => {
                      const cls = row.fulfillment_class ?? "daiwa";
                      const clsBadge = cls === "hitcamp" ? "bg-emerald-100 text-emerald-700"
                        : cls === "daiwa" ? "bg-blue-100 text-blue-700"
                        : cls === "decide" ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600";
                      const clsLabel = cls === "hitcamp" ? "우리샵"
                        : cls === "daiwa" ? "다이와"
                        : cls === "decide" ? "결정필요"
                        : "매칭필요";
                      return (
                        <div key={i} className="px-4 py-3 hover:bg-blue-50">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-bold ${clsBadge}`}>
                                {clsLabel}
                              </span>
                              <span className="font-mono text-xs text-indigo-600 font-semibold truncate">{row.order_no}</span>
                              <span className="text-xs text-gray-500 truncate">{row.receiver_name}</span>
                            </div>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap">
                              샵 {row.hitcamp_qty ?? 0} / 다이와 {row.daiwa_qty ?? 0}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 mt-0.5 truncate">
                            <span className="font-mono text-xs text-gray-400 mr-1">{row.jan_code}</span>
                            {row.product_name}
                            <span className="text-gray-400 ml-1">× {row.quantity}</span>
                          </p>
                        </div>
                      );
                    })}
                    {daiwaDsPreview.total_rows > 8 && (
                      <div className="px-4 py-2.5 bg-gray-50 text-center">
                        <button
                          onClick={() => setDaiwaDsModal(true)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                        >
                          나머지 {daiwaDsPreview.total_rows - 8}건 더 보기 ▼
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {daiwaDsPreview && daiwaDsPreview.total_orders > 0 && (
                  <div className="px-4 py-2.5 border-t border-blue-100 bg-blue-50 flex justify-end">
                    <a
                      href="/daiwa"
                      className="text-xs text-blue-700 font-semibold hover:text-blue-900"
                    >
                      다이와 주문 관리 →
                    </a>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* 다이와 직배 전체 모달 */}
          {daiwaDsModal && daiwaDsPreview && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <h3 className="text-base font-bold text-gray-800">
                    🚢 다이와 주문건 전체 — {daiwaDsPreview.total_orders}건 ({daiwaDsPreview.total_rows}행)
                  </h3>
                  <button
                    onClick={() => setDaiwaDsModal(false)}
                    className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
                  {daiwaDsPreview.sample_rows.map((row, i) => {
                    const cls = row.fulfillment_class ?? "daiwa";
                    const clsBadge = cls === "hitcamp" ? "bg-emerald-100 text-emerald-700"
                      : cls === "daiwa" ? "bg-blue-100 text-blue-700"
                      : cls === "decide" ? "bg-amber-100 text-amber-700"
                      : "bg-gray-100 text-gray-600";
                    const clsLabel = cls === "hitcamp" ? "우리샵 출고"
                      : cls === "daiwa" ? "다이와 직배"
                      : cls === "decide" ? "결정 필요"
                      : "매칭 필요";
                    return (
                      <div key={i} className="px-6 py-3 hover:bg-gray-50">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-bold ${clsBadge}`}>
                              {clsLabel}
                            </span>
                            <span className="font-mono text-xs text-indigo-600">{row.order_no}</span>
                            <span className="text-sm font-medium text-gray-700 truncate">{row.receiver_name}</span>
                            <span className="text-xs text-gray-400">{row.phone}</span>
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap">
                            샵 {row.hitcamp_qty ?? 0} / 다이와 {row.daiwa_qty ?? 0}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          <span className="font-mono">{row.jan_code}</span>
                          {" · "}{row.product_name}
                          {" · "}<span className="font-medium">×{row.quantity}</span>
                        </p>
                      </div>
                    );
                  })}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                  <a href="/daiwa" className="text-sm text-blue-600 font-semibold hover:text-blue-800">
                    다이와 주문 관리 →
                  </a>
                  <button
                    onClick={() => setDaiwaDsModal(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          )}

          {s && !loading && (
            <div className="space-y-8">
              {/* 날짜 배지 */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1">
                  📅 {s.date} 기준
                </span>
                {s.picking.hold_pending > 0 && (
                  <span className="text-xs font-bold bg-red-100 text-red-700 border border-red-200 rounded-lg px-3 py-1 animate-pulse">
                    🚫 출고중지 요청 {s.picking.hold_pending}건 대기 중
                  </span>
                )}
                {s.picking.blocked_picks > 0 && (
                  <span className="text-xs font-bold bg-orange-100 text-orange-700 border border-orange-200 rounded-lg px-3 py-1">
                    ⚠ 차단된 검수 {s.picking.blocked_picks}건
                  </span>
                )}
              </div>

              {/* 목표 미달 경고 */}
              {targetAlerts && targetAlerts.at_risk_count > 0 && (
                <section>
                  <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base">🚨</span>
                      <p className="text-sm font-bold text-red-700">
                        이번 달 목표 미달 핵심 지표 {targetAlerts.at_risk_count}건
                      </p>
                      <a href="/reports" className="ml-auto text-xs text-red-500 hover:text-red-700 underline">
                        리포트 보기
                      </a>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {targetAlerts.items.slice(0, 3).map((item) => (
                        <div key={item.metric} className="bg-white rounded-lg border border-red-200 px-4 py-3">
                          <p className="text-xs font-semibold text-red-600 mb-1">{item.metric_label}</p>
                          <p className="text-lg font-bold text-red-700">{Math.round(item.rate * 100)}%</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            실적 {item.actual.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} /
                            목표 {item.target.toLocaleString("ko-KR", { maximumFractionDigits: 0 })}
                          </p>
                        </div>
                      ))}
                    </div>
                    {targetAlerts.at_risk_count > 3 && (
                      <p className="text-xs text-red-500 mt-2">외 {targetAlerts.at_risk_count - 3}건 더 있습니다.</p>
                    )}
                  </div>
                </section>
              )}

              {/* 1. 주문/매출 — 한국식 금액 표기 (₩M/만 폐지) */}
              <section>
                <SectionTitle>📦 주문 / 매출 ({s.date})</SectionTitle>
                <div className="grid grid-cols-7 gap-3">
                  <StatCard label="주문건수" value={`${fmt(s.orders.count)}건`} color="blue" />
                  <StatCard label="상품매출" value={formatKRW(s.orders.total_product_amount)} color="green" />
                  <StatCard label="배송비" value={formatKRW(s.orders.shipping_fee + s.orders.additional_shipping_fee)} color="teal"
                    sub={`기본 ${formatKRW(s.orders.shipping_fee)} · 추가 ${formatKRW(s.orders.additional_shipping_fee)}`} />
                  <StatCard label="결제금액" value={formatKRW(s.orders.paid_amount)} color="blue" />
                  <StatCard label="수수료" value={formatKRW(-Math.abs(s.orders.fee_amount))} color="orange" />
                  <StatCard label="정산금액" value={formatKRW(s.orders.settlement_amount)} color="purple" />
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs font-semibold text-gray-500 mb-2">매출 구성</p>
                    <div className="space-y-1 text-xs text-gray-600">
                      <div className="flex justify-between">
                        <span>상품</span>
                        <span className="font-mono">{formatKRW(s.orders.total_product_amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>배송비</span>
                        <span className="font-mono">{formatKRW(s.orders.shipping_fee)}</span>
                      </div>
                      <div className="flex justify-between text-red-500">
                        <span>수수료</span>
                        <span className="font-mono">{formatKRW(-Math.abs(s.orders.fee_amount))}</span>
                      </div>
                      <div className="flex justify-between font-bold text-gray-800 border-t border-gray-100 pt-1 mt-1">
                        <span>정산</span>
                        <span className="font-mono">{formatKRW(s.orders.settlement_amount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* 출고 검수 / 반품·교환 / AS 관리 / 구매·입고 / 재고현황 / 일일 마감 체크리스트 섹션은
                  최신 운영 흐름과 맞지 않아 제거됨 (운영대시보드 단순화).
                  필요한 정보는 사이드바 각 도메인 화면에서 확인.
                  주: 출고중지 요청 대기 / 차단된 검수 카운트는 위쪽 날짜 배지 옆 배너에 그대로 유지됨. */}

              {/* 오늘 일정 / 휴가자 */}
              {todaySummary && (todaySummary.today_events.length > 0 || todaySummary.today_leave_count > 0) && (
                <section>
                  <SectionTitle>📅 오늘 현황 ({todaySummary.today})</SectionTitle>
                  <div className="grid grid-cols-2 gap-4">
                    {/* 오늘 중요 일정 */}
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                        오늘 중요 일정
                        {todaySummary.today_events.length > 0 && (
                          <span className="ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs font-semibold">
                            {todaySummary.today_events.length}건
                          </span>
                        )}
                      </p>
                      {todaySummary.today_events.length === 0 ? (
                        <p className="text-xs text-gray-400">오늘 예정된 중요 일정이 없습니다</p>
                      ) : (
                        <div className="space-y-2">
                          {todaySummary.today_events.slice(0, 3).map((ev) => {
                            const typeColors: Record<string, string> = {
                              company_event: "bg-blue-100 text-blue-700",
                              deadline:      "bg-red-100 text-red-700",
                              holiday:       "bg-purple-100 text-purple-700",
                              personal_schedule: "bg-green-100 text-green-700",
                            };
                            const typeLabels: Record<string, string> = {
                              company_event: "행사",
                              deadline:      "마감",
                              holiday:       "공휴일",
                              personal_schedule: "개인",
                            };
                            // [FW-037] 휴가는 오른쪽 "오늘 휴가자" 에서만 센다(서버가 휴가 연동 일정을 today_events 에서 제외).
                            return (
                              <div key={ev.id} className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${typeColors[ev.event_type] ?? "bg-gray-100 text-gray-600"}`}>
                                  {typeLabels[ev.event_type] ?? ev.event_type}
                                </span>
                                <span className="text-sm text-gray-700 font-medium">{ev.title}</span>
                                {ev.owner_name && (
                                  <span className="text-xs text-gray-400">({ev.owner_name})</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* 오늘 휴가자 */}
                    <div className={`rounded-xl border p-4 ${todaySummary.today_leave_count > 0 ? "bg-yellow-50 border-yellow-200" : "bg-white border-gray-200"}`}>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                        오늘 휴가자
                        {todaySummary.today_leave_count > 0 && (
                          <span className="ml-2 bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded-full text-xs font-semibold">
                            {todaySummary.today_leave_count}명
                          </span>
                        )}
                      </p>
                      {todaySummary.today_leave_count === 0 ? (
                        <p className="text-xs text-gray-400">오늘 휴가인 직원이 없습니다</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {todaySummary.today_leave_employees.map((name) => (
                            <span
                              key={name}
                              className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 border border-yellow-300 text-sm font-medium px-3 py-1 rounded-full"
                            >
                              🌴 {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

            </div>
          )}
        </div>
      </main>
    </div>
  );
}

